import { memo, useEffect } from 'react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/20/solid';
import AuthForm from 'components/auth/tools/AuthForm';
import GameCard from 'components/games/tools/GameCard';
import NewHeroSection from 'components/hero/NewHeroSection';
import { Spinner } from 'components/ira/ui/Spinner';
import EmailConfirmationModal from 'components/modals/EmailConfirmationModal';
import NiceModal from 'components/modals/NiceModal';
import SubscriptionModal from 'components/modals/SubscriptionModal';
import useModalState from 'hooks/useModalState';
import { useMappedState } from 'redux-react-hook';
import { GUEST_ROUTES } from 'routes/routes';
import styled from 'styled-components';

import UserTemplate from '../../components/templates/UserTemplate/UserTemplate';
import { useGetMyLikedGamesIdsQuery, useGetPublicGamesQuery } from '../../services/api/gamesApi';
import { useGetTagByNameQuery } from '../../services/api/tagApi';

const BANNER_HEIGHT = 372;
const HEADED_HEIGHT = 96;

const getFormattedTagName = tag => {
  const splittedTagNameWoDash = tag.split('_').join(' ');
  return splittedTagNameWoDash;
};

const TagPage = () => {
  const { tagName } = useParams();
  const navigate = useNavigate();

  const { data: myLikedGamesIds = [] } = useGetMyLikedGamesIdsQuery();

  const {
    data: publicGames = [],
    isLoading: isLoadingAllGames,
    isError: isAllGamesError,
    isSuccess: isAllGamesFetchSuccess
  } = useGetPublicGamesQuery();

  const { data: tag,
    isLoading: isLoadingTag,
    isError: isErrorFetchingTag,
    isSuccess: isSuccessFetchingTag
  } = useGetTagByNameQuery(tagName);

  const [isOpenEmailConfirmationModal, openEmailConfirmationModal, closeEmailConfirmationModal] =
    useModalState();
  const [isOpenAuthModal, openAuthModal, closeAuthModal] = useModalState();
  const [isSubscriptionModalOpen, openSubscriptionModal, closeSubscriptionModal] = useModalState();

  const { currentUser, hasSubscription, isSignedIn } = useMappedState(state => {
    const currentUser = state.users.usersMap.get(state.users.currentUserId);
    const hasSubscription = currentUser?.hasSubscription;

    return {
      currentUser: state.users.usersMap.get(state.users.currentUserId),
      hasSubscription: hasSubscription,
      isSignedIn: state.users.currentUserId
    };
  });

  const formattedTagName = getFormattedTagName(tagName);
  const isSuccess = isAllGamesFetchSuccess || isSuccessFetchingTag;
  const isLoading = isLoadingTag || isLoadingAllGames;

  const handleCreateScenarioButtonClick = () => {
    if (!currentUser) {
      openAuthModal();
    } else if (!currentUser?.emailVerified) {
      openEmailConfirmationModal();
    } else {
      const searchParams = new URLSearchParams();
      if (tag?.name) {
        searchParams.set('tags', JSON.stringify([tag.name]));
      }
      navigate(`${GUEST_ROUTES.SCENARIOS_GENERATOR()}${searchParams.toString() ? '?' + searchParams.toString() : ''}`);
    }
  };

  const handleGameCardClick = game => {
    if (!game?.isLocked) {
      navigate(GUEST_ROUTES.ROLEPLAY(game?.alias));
    } else {
      if (isSignedIn) {
        openSubscriptionModal();
      } else {
        openAuthModal();
      }
    }
  };

  const gamesWithTag = publicGames.filter(game => game?.tags?.includes(tag?.name ?? ''));
  const formattedTitle = formattedTagName.charAt(0).toUpperCase() + formattedTagName.slice(1);

  useEffect(() => {
    if (isAllGamesError) {
      toast.error('Incorrect tag name');
      navigate('/');
    }
  }, [isAllGamesError, isErrorFetchingTag]);

  if (isLoading) {
    return (
      <UserTemplate hasFooter={false} currentName={'tag_page'}>
        <SpinnerContainer>
          <Spinner />;
        </SpinnerContainer>
      </UserTemplate>
    );
  }

  return (
    <UserTemplate hasFooter currentName={'tag_page'}>
      {isSuccess && (
        <>
          {!isSignedIn && (
            <BannerWrapper>
              <NewHeroSection isShortBackground isChooseButtonVisible={false} />
            </BannerWrapper>
          )}
          <ContentWrapper isSignedIn={isSignedIn}>
            <HeaderWrapper>
              <Title hasDescription={!!tag?.description}>{formattedTitle}</Title>
              {tag?.description && <TagDescription><ReactMarkdown>{tag.description}</ReactMarkdown ></TagDescription>}
            </HeaderWrapper>

            <ScenariosSection>
              <ScenariosSectionTitle>Scenarios by this tag</ScenariosSectionTitle>
              <div className="mx-auto box-border py-4">
                <div className="box-border grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
                  {gamesWithTag.map(game => {
                    const isLikedByMe = myLikedGamesIds.includes(game.id);
                    return (
                      <GameCard
                        key={game.id}
                        item={game}
                        onItemClick={handleGameCardClick}
                        isLikedByMe={isLikedByMe}
                        likesToShow={game?.likesNumber}
                        shouldShowLikes={!!game?.likesNumber}
                        hasSubscription={hasSubscription}
                        isVisibleTagSection
                      />
                    );
                  })}
                </div>
              </div>
            </ScenariosSection>
          </ContentWrapper>
          <CreateScenarioSection>
            <CreateScenarioPlaceholder id="create-scenario">
              <span className='text-center font-["Raleway"] text-4xl font-bold leading-9'>
                Create your Own Scenario
              </span>
              <OrangeButton onClick={handleCreateScenarioButtonClick}>
                <PlusIcon className="h-6 w-6" /> Create scenario
              </OrangeButton>
            </CreateScenarioPlaceholder>
          </CreateScenarioSection>
          <EmailConfirmationModal
            isOpen={isOpenEmailConfirmationModal}
            onClose={closeEmailConfirmationModal}
          />
          {isOpenAuthModal && (
            <NiceModal onClose={closeAuthModal}>
              <ModalInnerPlaceholder>
                <AuthForm onClose={closeAuthModal} />
              </ModalInnerPlaceholder>
            </NiceModal>
          )}
          {isSubscriptionModalOpen && (
            <SubscriptionModal
              analyticsData={{
                rendered_for_reason: 'premium_roleplay_click'
              }}
              isOpen
              onClose={closeSubscriptionModal}
            />
          )}
        </>
      )}
    </UserTemplate>
  );
};

export default memo(TagPage);

const BannerWrapper = styled.div`
  overflow-y: hidden;
  position: absolute;
  top: 120px;
  right: 0;
  left: 0;
  @media (max-width: 620px) {
    position: relative;
    height: calc(100vh - 50px);
    margin-bottom: 40px;
  }
`;

const ContentWrapper = styled.div`
  padding-top: ${props =>
    props.isSignedIn ? '20px' : `calc(${BANNER_HEIGHT}px + ${HEADED_HEIGHT}px)`};
  @media (max-width: 620px) {
    padding-top: 120px;
  }
`;

const Title = styled.h2`
  font-size: 42px;
  line-height: 40px;
  font-family: Raleway;
  font-weight: bold;
  color: white;
  padding-bottom: ${({ hasDescription }) => (hasDescription ? '16px' : '0')};

  @media (max-width: 720px) {
    font-size: 38px;
    line-height: 36px;
  }
`;

const TagDescription = styled.p`
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
  color: #a9a9a9;
  // max-width: 70%;
  white-space: pre-wrap;
    //   font-size: 16px;
    // line-height: 18px;
  @media (max-width: 720px) {
    max-width: 100%;
  }
`;

const HeaderWrapper = styled.div`
  padding-bottom: 60px;
`;

const ScenariosSection = styled.div`
  padding-bottom: 60px;
`;

const ScenariosSectionTitle = styled.h3`
  font-family: Raleway;
  font-size: 20px;
  font-weight: 700;
  line-height: 22px;
  text-align: left;
  color: white;
  margin-bottom: 32px;
`;

const CreateScenarioPlaceholder = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  color: white;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 32px 46px;
  background-color: rgba(70, 55, 50, 1);
  border-radius: 12px;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23FF7E55FF' stroke-width='2' stroke-dasharray='12%2c 16' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  @media (max-width: 720px) {
    padding: 24px 32px;
  }
`;

const OrangeButton = styled.button`
  background-color: rgba(255, 126, 85, 1);
  width: fit-content;
  padding: 12px 24px;
  border-radius: 100px;
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CreateScenarioSection = styled.div`
  margin: 24px 0;
`;

const ModalInnerPlaceholder = styled.div`
  width: 380px;
  padding: 30px;
  box-sizing: border-box;
  @media (max-width: 400px) {
    width: 100%;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
`;
